import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { LoggerService } from '../../../services/logger.service';

import { RouterLink } from '@angular/router';
import {
  ReactiveFormsModule,
  FormBuilder,
  Validators,
  FormGroup,
  AbstractControl,
} from '@angular/forms';
import { CompanyService } from '../../../services/company.service';
import { Company } from '../../../services/company.service';
import { AuthService } from '../../../services/auth.service';
import { GoogleAuthService } from '../../../services/google-auth.service';
import { lastValueFrom, Subject, takeUntil } from 'rxjs';
import { SaveButtonComponent } from '../../save-button/save-button.component';
import { ImageCropperModalComponent } from '../../image-cropper-modal/image-cropper-modal.component';
import { ImageCropData } from '../../../services/voucher-product.service';
import { environment } from '../../../../environments/environment';
import { COUNTRIES, Country, requiresVatId } from '../../../config/countries.config';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideCheck,
  lucideDot,
  lucideEye,
  lucideEyeOff,
  lucideImage,
  lucideImagePlus,
  lucideInfo,
  lucideMail,
} from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmBadgeImports } from '@spartan-ng/helm/badge';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmInputGroupImports } from '@spartan-ng/helm/input-group';
import { HlmNativeSelectImports } from '@spartan-ng/helm/native-select';
import { HlmSeparatorImports } from '@spartan-ng/helm/separator';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
function passwordMatchValidator(control: AbstractControl) {
  const pw = control.get('new_password');
  const confirm = control.get('confirm_password');
  if (pw && confirm && pw.value !== confirm.value) {
    confirm.setErrors({ mismatch: true });
  } else if (confirm?.errors?.['mismatch']) {
    confirm.setErrors(null);
  }
  return null;
}

@Component({
  selector: 'app-company-settings',
  standalone: true,
  imports: [
    RouterLink,
    ReactiveFormsModule,
    SaveButtonComponent,
    ImageCropperModalComponent,
    NgIcon,
    HlmAlertImports,
    HlmBadgeImports,
    HlmButtonImports,
    HlmCardImports,
    HlmFieldImports,
    HlmInputImports,
    HlmInputGroupImports,
    HlmNativeSelectImports,
    HlmSeparatorImports,
    HlmSpinnerImports,
    HlmTypographyImports,
  ],
  providers: [
    provideIcons({
      lucideCheck,
      lucideDot,
      lucideEye,
      lucideEyeOff,
      lucideImage,
      lucideImagePlus,
      lucideInfo,
      lucideMail,
    }),
  ],
  templateUrl: './company-settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./company-settings.component.css'],
})
export class CompanySettingsComponent implements OnInit, OnDestroy {
  /** Set while an agency works in the account (see AuthService.actingPartnerName). */
  readonly actingPartnerName = inject(AuthService).actingPartnerName;

  private destroy$ = new Subject<void>();
  private initialFormValues: any;
  loading = false;
  companyForm: FormGroup;
  personalInfoChanged = false;
  billingAddressChanged = false;

  company: Company | null = null;

  // Email change
  emailForm: FormGroup;
  emailLoading = false;
  emailChangePending = false;
  pendingEmail = '';
  showEmailPassword = false;
  currentEmail = '';

  // Password change
  passwordForm: FormGroup;
  passwordLoading = false;
  showCurrentPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;

  // Set first password (passwordless Google accounts, via e-mail link)
  setPasswordLoading = false;
  setPasswordEmailSent = false;
  setPasswordError: string | null = null;

  // Google account linking
  googleLinkAvailable = false;
  googleLinkLoading = false;
  googleLinkError: string | null = null;
  googleLinkSuccess: string | null = null;
  private renderedGoogleBtnEl: HTMLElement | null = null;

  // Setter instead of ngAfterViewInit: the container lives in an *ngIf branch
  // that reappears after unlinking, so the button must render each time
  @ViewChild('googleLinkBtn') set googleLinkBtn(el: ElementRef<HTMLElement> | undefined) {
    if (el && el.nativeElement !== this.renderedGoogleBtnEl) {
      this.renderedGoogleBtnEl = el.nativeElement;
      this.renderGoogleLinkButton(el.nativeElement);
    }
  }

  // Logo upload
  logoPreview: string | null = null;
  logoFile: File | null = null;
  logoLoading = false;
  logoSuccess = false;
  logoError: string | null = null;
  showLogoCropper = false;
  logoFileForCropping: File | null = null;

  @ViewChild('personalInfoSaveButton') personalInfoSaveButton!: SaveButtonComponent;
  @ViewChild('billingAddressSaveButton') billingAddressSaveButton!: SaveButtonComponent;
  @ViewChild('pwChangeSaveButton') pwChangeSaveButton!: SaveButtonComponent;
  @ViewChild('emailSaveButton') emailSaveButton!: SaveButtonComponent;

  countries = COUNTRIES;

  constructor(
    private companyService: CompanyService,
    private authService: AuthService,
    private googleAuthService: GoogleAuthService,
    private fb: FormBuilder,
    private logger: LoggerService,
  ) {
    this.companyForm = this.fb.group({
      first_name: ['', Validators.required],
      last_name: ['', Validators.required],
      phone: ['', Validators.required],
      website: [
        '',
        [Validators.pattern(/^(https?:\/\/)?[\w\-]+(\.[\w\-]+)*\.[\w\-]{2,}([/#?].*)?$/)],
      ],
      company_name: [''],
      vat_id: [''],
      billing_street: ['', Validators.required],
      billing_city: ['', Validators.required],
      billing_zip: ['', Validators.required],
      billing_country: ['DE', Validators.required],
    });

    this.emailForm = this.fb.group({
      new_email: ['', [Validators.required, Validators.email]],
      current_password: ['', Validators.required],
    });

    this.passwordForm = this.fb.group(
      {
        current_password: ['', Validators.required],
        new_password: [
          '',
          [
            Validators.required,
            Validators.minLength(8),
            Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/),
          ],
        ],
        confirm_password: ['', Validators.required],
      },
      { validators: passwordMatchValidator },
    );

    this.companyForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.checkFormChanged());
  }

  ngOnInit() {
    // The store already holds the company — this used to fetch the same record a
    // second time via getCurrentCompanyFromAuth() while the store was loading it.
    this.loading = true;
    this.companyService.company$.pipe(takeUntil(this.destroy$)).subscribe((company) => {
      const first = !this.company;
      this.company = company;
      if (first) this.populateForm(company);
      this.loading = false;
    });
  }

  private async renderGoogleLinkButton(container: HTMLElement) {
    try {
      await this.googleAuthService.renderButton(
        container,
        { text: 'continue_with' },
        (credential) => this.onGoogleLinkCredential(credential),
      );
      this.googleLinkAvailable = true;
    } catch (error) {
      this.logger.error('Google linking not available:', error);
    }
  }

  /** True unless the account is Google-SSO-only and has no password yet */
  get hasPassword(): boolean {
    return this.company?.has_password !== false;
  }

  /** True when a Google identity is linked to the account */
  get hasGoogle(): boolean {
    return this.company?.has_google === true;
  }

  private get selectedBillingCountry(): Country | undefined {
    const code = this.companyForm.get('billing_country')?.value;
    return this.countries.find((c) => c.code === code);
  }

  /** Example VAT ID for the selected billing country */
  get vatIdPlaceholder(): string {
    return this.selectedBillingCountry?.vatIdExample ?? COUNTRIES[0].vatIdExample;
  }

  /**
   * Whether the selected billing country makes the UST-ID mandatory. Invoices
   * outside Germany are reverse-charge and cannot be issued without it.
   */
  get vatIdRequiredForCountry(): boolean {
    return requiresVatId(this.companyForm.get('billing_country')?.value);
  }

  /** Hint under the UST-ID field, phrased for the selected billing country */
  get vatIdHint(): string {
    const country = this.selectedBillingCountry;
    if (!country || !this.vatIdRequiredForCountry) {
      return 'Nur erforderlich, wenn Deine Rechnungsadresse außerhalb Deutschlands liegt.';
    }
    return `Für Unternehmen in ${country.name} erforderlich (z.B. ${country.vatIdExample})`;
  }

  /**
   * A Stripe connected account's country is fixed at creation, so the backend
   * rejects a later change. Lock the field instead of letting the save fail.
   */
  get billingCountryLocked(): boolean {
    return !!this.company?.stripe_account_id;
  }

  async onGoogleLinkCredential(credential: string) {
    this.googleLinkLoading = true;
    this.googleLinkError = null;
    this.googleLinkSuccess = null;
    try {
      const result = await lastValueFrom(this.authService.linkGoogle(credential));
      this.googleLinkSuccess = result?.message || 'Google-Konto erfolgreich verknüpft.';
      await this.companyService.reloadCompanyData();
    } catch (error: any) {
      this.logger.error('Google linking failed:', error);
      this.googleLinkError =
        error.error?.detail || 'Die Verknüpfung mit Google ist fehlgeschlagen.';
    } finally {
      this.googleLinkLoading = false;
    }
  }

  async onUnlinkGoogle() {
    this.googleLinkLoading = true;
    this.googleLinkError = null;
    this.googleLinkSuccess = null;
    try {
      const result = await lastValueFrom(this.authService.unlinkGoogle());
      this.googleLinkSuccess = result?.message || 'Google-Verknüpfung aufgehoben.';
      await this.companyService.reloadCompanyData();
    } catch (error: any) {
      this.logger.error('Google unlinking failed:', error);
      this.googleLinkError =
        error.error?.detail || 'Die Google-Verknüpfung konnte nicht aufgehoben werden.';
    } finally {
      this.googleLinkLoading = false;
    }
  }

  private checkFormChanged() {
    if (!this.initialFormValues) return;
    const personalFields = ['first_name', 'last_name', 'phone', 'website'];
    this.personalInfoChanged = personalFields.some(
      (k) => this.initialFormValues[k] !== this.companyForm.get(k)?.value,
    );
    const billingFields = [
      'company_name',
      'vat_id',
      'billing_street',
      'billing_city',
      'billing_zip',
      'billing_country',
    ];
    this.billingAddressChanged = billingFields.some(
      (k) => this.initialFormValues[k] !== this.companyForm.get(k)?.value,
    );
  }

  private populateForm(company: Company) {
    this.currentEmail = company.email || '';
    this.companyForm.patchValue(
      {
        first_name: company.first_name,
        last_name: company.last_name,
        phone: company.phone,
        website: company.website,
        company_name: company.company_name || '',
        vat_id: company.vat_id || '',
        billing_street: company.billing_street || '',
        billing_city: company.billing_city || '',
        billing_zip: company.billing_zip || '',
        billing_country: company.billing_country || 'DE',
      },
      { emitEvent: false },
    );
    this.initialFormValues = this.companyForm.value;
    this.personalInfoChanged = false;
    this.billingAddressChanged = false;
  }

  // --- Section saves ---

  onSubmitPersonalInfo() {
    const fields = ['first_name', 'last_name', 'phone', 'website'];
    if (!fields.every((f) => this.companyForm.get(f)?.valid)) return;
    this.loading = true;
    this.personalInfoSaveButton.hideMessages();
    const formData = { ...this.companyForm.value };
    if (formData.website && !formData.website.match(/^https?:\/\//)) {
      formData.website = `https://${formData.website}`;
    }
    this.saveSection(
      formData,
      () => {
        this.personalInfoChanged = false;
        this.personalInfoSaveButton.showSuccessMessage();
      },
      (err) => this.personalInfoSaveButton.showErrorMessage(err),
    );
  }

  onSubmitBillingAddress() {
    const fields = ['billing_street', 'billing_city', 'billing_zip', 'billing_country'];
    if (!fields.every((f) => this.companyForm.get(f)?.valid)) return;
    this.loading = true;
    this.billingAddressSaveButton.hideMessages();
    this.saveSection(
      this.companyForm.value,
      () => {
        this.billingAddressChanged = false;
        this.billingAddressSaveButton.showSuccessMessage();
      },
      (err) => this.billingAddressSaveButton.showErrorMessage(err),
    );
  }

  private saveSection(formData: any, onSuccess: () => void, onError?: (msg: string) => void) {
    this.companyService.updateCompanyDetails(formData).subscribe({
      next: () => {
        this.loading = false;
        this.initialFormValues = this.companyForm.value;
        onSuccess();
      },
      error: (error) => {
        const msg = error.error?.detail || 'Fehler beim Speichern. Bitte versuche es erneut.';
        this.logger.error('Error saving company details:', error);
        this.loading = false;
        onError?.(msg);
      },
    });
  }

  // --- Email change ---

  onSubmitEmailChange() {
    if (this.emailForm.invalid) {
      this.emailForm.markAllAsTouched();
      return;
    }
    this.emailLoading = true;
    this.emailSaveButton.hideMessages();
    const { new_email, current_password } = this.emailForm.value;
    this.authService.changeEmail(new_email, current_password).subscribe({
      next: () => {
        this.emailLoading = false;
        this.pendingEmail = new_email;
        this.emailChangePending = true;
        this.emailForm.reset();
        this.emailSaveButton.showSuccessMessage();
      },
      error: (err) => {
        this.emailLoading = false;
        const msg = err.error?.detail || 'Fehler beim Ändern der E-Mail-Adresse.';
        this.logger.error('Email change error:', err);
        this.emailSaveButton.showErrorMessage(msg);
      },
    });
  }

  // --- Password change ---

  onSubmitPasswordChange() {
    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      return;
    }
    this.passwordLoading = true;
    this.pwChangeSaveButton.hideMessages();
    const { current_password, new_password } = this.passwordForm.value;
    this.authService.changePassword(current_password, new_password).subscribe({
      next: () => {
        this.passwordLoading = false;
        this.passwordForm.reset();
        this.pwChangeSaveButton.showSuccessMessage();
      },
      error: (err) => {
        this.passwordLoading = false;
        const msg = err.error?.detail || 'Fehler beim Ändern des Passworts.';
        this.logger.error('Password change error:', err);
        this.pwChangeSaveButton.showErrorMessage(msg);
      },
    });
  }

  // Passwordless (Google-only) accounts set their first password via the
  // e-mail-verified reset flow — a session alone must not mint a credential
  onRequestSetPassword() {
    this.setPasswordLoading = true;
    this.setPasswordError = null;
    this.authService.requestPasswordReset(this.currentEmail).subscribe({
      next: () => {
        this.setPasswordLoading = false;
        this.setPasswordEmailSent = true;
      },
      error: (err) => {
        this.setPasswordLoading = false;
        this.logger.error('Set-password mail error:', err);
        this.setPasswordError =
          'Die E-Mail konnte nicht gesendet werden. Bitte versuche es erneut.';
      },
    });
  }

  // Logo upload
  onLogoFileSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      this.logoError = 'Ungültiger Dateityp. Nur JPEG, PNG und WebP sind erlaubt.';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      this.logoError = 'Datei zu groß. Maximale Größe ist 5 MB.';
      return;
    }
    this.logoError = null;
    this.logoFileForCropping = file;
    this.showLogoCropper = true;
  }

  onLogoCropComplete(event: { file: File; croppedFile: File; cropData: ImageCropData }) {
    this.showLogoCropper = false;
    this.logoFileForCropping = null;
    this.logoFile = event.croppedFile;
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.logoPreview = e.target.result;
    };
    reader.readAsDataURL(event.croppedFile);
  }

  onLogoCropCancel() {
    this.showLogoCropper = false;
    this.logoFileForCropping = null;
  }

  uploadLogo() {
    if (!this.logoFile) return;
    this.logoLoading = true;
    this.logoSuccess = false;
    this.logoError = null;
    this.companyService.uploadCompanyLogo(this.logoFile).subscribe({
      next: () => {
        this.logoLoading = false;
        this.logoSuccess = true;
        this.logoFile = null;
        setTimeout(() => {
          this.logoSuccess = false;
        }, 3000);
      },
      error: (err) => {
        this.logoLoading = false;
        this.logoError = err.error?.detail || 'Fehler beim Hochladen des Logos.';
        this.logger.error('Logo upload error:', err);
      },
    });
  }

  getLogoUrl(path: string): string {
    const clean = path.startsWith('uploads/') ? path.substring(8) : path;
    return `${environment.apiUrl}/images/${clean}`;
  }

  // Password requirement helpers
  /** Field errors appear once the field has been touched. */
  isInvalid(form: FormGroup, controlName: string): boolean {
    const control = form.get(controlName);
    return !!control?.invalid && !!control?.touched;
  }

  hasMinLength(): boolean {
    return (this.passwordForm.get('new_password')?.value || '').length >= 8;
  }
  hasUppercase(): boolean {
    return /[A-Z]/.test(this.passwordForm.get('new_password')?.value || '');
  }
  hasLowercase(): boolean {
    return /[a-z]/.test(this.passwordForm.get('new_password')?.value || '');
  }
  hasNumber(): boolean {
    return /\d/.test(this.passwordForm.get('new_password')?.value || '');
  }
  passwordsMatch(): boolean {
    const pw = this.passwordForm.get('new_password')?.value || '';
    const confirm = this.passwordForm.get('confirm_password')?.value || '';
    return pw === confirm && pw.length > 0;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
